import { computed, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'

import { useQuery } from '@/composables/api'
import { setDatadogUserAndAccount } from '@/plugins/datadog'
import { identifySurvicateUser } from '@/plugins/survicate'
import useAuthStore from '@/stores/authStore'
import type { CustomerUser } from '@/types/customer-users'

/**
 * Datadog RUM and Survicate identity for the signed-in customer. The token carries id and name;
 * the email and the creation date come from the user resource, read through the same query the
 * profile surfaces use, so a profile save (auto-invalidation of /customer--users) refreshes the
 * identity without any write-back. Clearing on logout is useAuthEffects watcher 1's job.
 */
export function useAnalyticsIdentity() {
  const authStore = useAuthStore()
  const isAuthenticated = computed(() => authStore.selectedToken != null)

  // Disabled while logged out: the query must never run without a token.
  const userQuery = useQuery('/customer--users', undefined, { enabled: isAuthenticated })
  const user = computed<CustomerUser | undefined>(() => userQuery.data.value?._items?.[0])

  const datadogUserPayload = computed(() => {
    const payload = authStore.selectedToken?.payload
    return {
      id: payload?.u_id,
      // The customer token carries no email; it comes from the user resource.
      email: user.value?.email_data?.email,
      name: payload?.u_name,
      solicitor: false,
    }
  })
  watchDebounced(
    datadogUserPayload,
    (value) => {
      if (value.id == null) return
      try {
        setDatadogUserAndAccount({ ...value, id: value.id })
      } catch (err) {
        console.error('Failed to set Datadog user:', err)
      }
    },
    { debounce: 500, immediate: true }
  )

  watch(
    () => [user.value?._id, user.value?.email_data?.email, user.value?._created] as const,
    ([userId, email, created]) => {
      if (userId != null && email != null && email !== '' && created != null) {
        identifySurvicateUser({
          user_id: userId,
          email,
          created_at: new Date(created),
          solicitor: false,
        })
      }
    }
  )
}
